import { useEffect, useRef, useState } from "react";
import { Download, FileText, MapPin, Pause, Play } from "lucide-react";
import { fileUrl } from "@/lib/upload";
import { slowNetwork } from "@/lib/richtext";

export function useFileUrl(id, auto = true) {
  const [url, setUrl] = useState(null);
  const [want, setWant] = useState(auto);
  useEffect(() => {
    if (id && want && !url) fileUrl(id).then(setUrl).catch(() => {});
  }, [id, want, url]);
  return [url, () => setWant(true)];
}

export function MediaFile({ id, kind, meta = {} }) {
  const [url, load] = useFileUrl(id, !slowNetwork() || kind === "document");
  if (kind === "document") {
    return (
      <a data-testid="msg-document" href={url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 border border-mr-border rounded-[3px] bg-mr-bg min-w-[200px]">
        <FileText size={22} className="text-mr-accent shrink-0" />
        <span className="text-sm truncate flex-1">{meta.filename || "Document"}</span>
        <Download size={16} className="text-mr-muted" />
      </a>
    );
  }
  if (!url) {
    return (
      <button data-testid="msg-media-load" onClick={load} className="w-56 h-36 grid place-items-center bg-mr-bg border border-mr-border text-sm text-mr-text2 rounded-[3px]">
        <span className="flex items-center gap-2"><Download size={16} /> {kind === "video" ? "Charger la vidéo" : "Charger la photo"}</span>
      </button>
    );
  }
  if (kind === "video") return <video data-testid="msg-video" src={url} controls preload="metadata" className="max-w-[260px] max-h-72 rounded-[3px]" />;
  return <a href={url} target="_blank" rel="noopener noreferrer"><img data-testid="msg-image" src={url} alt="" loading="lazy" className="max-w-[260px] max-h-72 object-cover rounded-[3px]" /></a>;
}

const SPEEDS = [1, 1.5, 2];
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function VoicePlayer({ id, meta = {} }) {
  const [url, load] = useFileUrl(id, !slowNetwork());
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [speed, setSpeed] = useState(1);
  const bars = meta.waveform?.length ? meta.waveform : Array.from({ length: 32 }, () => 0.3);
  const dur = meta.duration || audio.current?.duration || 1;
  const toggle = async () => {
    if (!url) {
      load();
      return;
    }
    if (playing) audio.current.pause();
    else await audio.current.play();
  };
  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed, url]);
  return (
    <div className="flex items-center gap-2 min-w-[230px]" data-testid="voice-player">
      <button data-testid="voice-play-button" aria-label={playing ? "Pause" : "Lecture"} onClick={toggle} className="w-9 h-9 shrink-0 rounded-full bg-mr-accent text-mr-on-accent grid place-items-center">
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="flex items-end gap-[2px] h-7 flex-1" onClick={(e) => {
        if (!audio.current?.duration) return;
        const r = e.currentTarget.getBoundingClientRect();
        audio.current.currentTime = ((e.clientX - r.left) / r.width) * audio.current.duration;
      }}>
        {bars.map((b, i) => (
          <span key={i} className={`w-[3px] rounded-full ${i / bars.length <= pos / dur ? "bg-mr-accent" : "bg-mr-muted/50"}`} style={{ height: `${Math.max(12, b * 100)}%` }} />
        ))}
      </div>
      <span className="text-[11px] tabular text-mr-muted w-9">{fmt(playing || pos ? pos : dur)}</span>
      <button data-testid="voice-speed-button" onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
        className="text-[11px] font-bold px-1.5 h-6 rounded-full border border-mr-border tabular">{speed}x</button>
      {url && <audio ref={audio} src={url} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPos(0); }} onTimeUpdate={(e) => setPos(e.target.currentTime)} />}
    </div>
  );
}

export function LocationCard({ meta }) {
  const href = `https://www.openstreetmap.org/?mlat=${meta.lat}&mlon=${meta.lng}#map=16/${meta.lat}/${meta.lng}`;
  return (
    <a data-testid="msg-location" href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 border border-mr-border rounded-[3px] bg-mr-bg">
      <MapPin size={20} className="text-mr-accent" />
      <span className="text-sm">{meta.label || "Position partagée"}<br /><span className="text-xs text-mr-muted tabular">{Number(meta.lat).toFixed(5)}, {Number(meta.lng).toFixed(5)}</span></span>
    </a>
  );
}
